import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, ComposedChart, Bar, Line, Legend } from 'recharts';
import { ArrowUpRight, ArrowDownRight, Circle } from 'lucide-react';

// Monthly tCO2e for a 16-rack small DC on 89% renewable mix
const monthlyTrendData = [
    { month: 'Jan', actual: 7.8, target: 7.5 },
    { month: 'Feb', actual: 7.4, target: 7.4 },
    { month: 'Mar', actual: 8.1, target: 7.3 },
    { month: 'Apr', actual: 8.6, target: 7.2 },
    { month: 'May', actual: 9.2, target: 7.1 },
    { month: 'Jun', actual: 9.8, target: 7.0 },
    { month: 'Jul', actual: 10.4, target: 6.9 },
    { month: 'Aug', actual: 10.1, target: 6.8 },
    { month: 'Sep', actual: 8.9, target: 6.7 },
    { month: 'Oct', actual: 8.2, target: 6.6 },
    { month: 'Nov', actual: 7.6, target: 6.5 },
    { month: 'Dec', actual: 7.3, target: 6.4 },
];

const carbonCreditsData = [
    { month: 'Jan', credits: 9, trend: 360 },
    { month: 'Feb', credits: 10, trend: 400 },
    { month: 'Mar', credits: 12, trend: 480 },
    { month: 'Apr', credits: 11, trend: 440 },
    { month: 'May', credits: 14, trend: 560 },
    { month: 'Jun', credits: 15, trend: 600 },
];

const sourceData = [
    { name: 'Electricity (Scope 2)', value: 73, color: '#60a5fa' },
    { name: 'Refrigerants (Scope 1)', value: 14, color: '#fbbf24' },
    { name: 'Backup Gen Diesel (Scope 1)', value: 7, color: '#f87171' },
    { name: 'Business Travel (Scope 3)', value: 6, color: '#94a3b8' },
];

// Annual tCO2e — small DC trajectory toward net-zero by 2028
const timelineData = [
    { year: '2021', value: 142 },
    { year: '2022', value: 118 },
    { year: '2023', value: 95 },
    { year: '2024', value: 78 },
    { year: '2025', value: 52 },
];

const StatCard = ({ title, value, subValue, change, trend, trendColor }: any) => {
    const getTrendColor = (color: string) => {
        switch (color) {
            case 'red': return 'text-red-500';
            case 'green': return 'text-green-500';
            case 'orange': return 'text-orange-500';
            default: return 'text-zinc-400';
        }
    };

    return (
        <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-5 flex flex-col justify-between shadow-sm min-h-[120px]">
            <div className="text-zinc-500 text-[11px] font-bold tracking-wider mb-2 uppercase">{title}</div>
            <div className="flex items-baseline gap-2 mb-4">
                <div className="text-2xl font-bold text-white leading-none">{value}</div>
                <div className="text-[10px] text-zinc-500">{subValue}</div>
            </div>
            <div className={`text-[10px] font-semibold flex items-center gap-1 ${getTrendColor(trendColor)}`}>
                {trend === 'up' ? <ArrowUpRight size={12} /> : trend === 'down' ? <ArrowDownRight size={12} /> : <Circle size={8} className="fill-current" />}
                {change}
            </div>
        </div>
    );
};

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#1a1d24] border border-[#2e323a] p-3 rounded-lg shadow-xl">
                <p className="text-zinc-400 text-xs mb-2">{label}</p>
                {payload.map((entry: any, index: number) => (
                    <div key={index} className="flex items-center gap-2 text-sm font-semibold text-white">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                        {entry.name}: {entry.value}
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const CarbonEmissionOverview = () => {
    return (
        <div className="w-full h-full flex flex-col gap-4 text-white pb-10">
            {/* Top 6 Cards */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                <StatCard title="TOTAL CO2" value="78t" subValue="YTD 2025" change="- 18% vs last year" trend="down" trendColor="green" />
                <StatCard title="CARBON INTENSITY" value="0.097" subValue="kg/kWh" change="Target: 0.08" trend="none" trendColor="none" />
                <StatCard title="SCOPE 1" value="21t" subValue="Direct Emissions" change="27% of Total" trend="none" trendColor="none" />
                <StatCard title="SCOPE 2" value="57t" subValue="Indirect Emissions" change="73% of Total" trend="none" trendColor="none" />
                <StatCard title="RENEWABLE MIX" value="89%" subValue="Energy Source" change="+ 9% vs Target" trend="up" trendColor="green" />
                <StatCard title="PUE RATIO" value="1.42" subValue="Efficiency" change="Target: < 1.40" trend="none" trendColor="orange" />
            </div>

            {/* Row 2: Monthly Trends and Credits */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Monthly CO2e Emissions Trend */}
                <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-6 col-span-2 flex flex-col min-h-[360px]">
                    <div className="flex flex-col mb-4">
                        <h3 className="font-bold text-sm">Monthly CO2e Emissions Trend</h3>
                        <p className="text-[10px] text-zinc-500 mt-1">6-month rolling average with Target Trajectory</p>
                    </div>

                    <div className="flex-1 w-full relative -ml-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={monthlyTrendData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4} />
                                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#2e323a" vertical={false} />
                                <XAxis dataKey="month" stroke="#71717a" fontSize={10} tickLine={false} axisLine={false} />
                                <YAxis stroke="#71717a" fontSize={10} tickLine={false} axisLine={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: '10px' }} verticalAlign="top" iconType="circle" />

                                <Area
                                    type="monotone"
                                    name="Actual Emissions"
                                    dataKey="actual"
                                    stroke="#ef4444"
                                    strokeWidth={2}
                                    fillOpacity={1}
                                    fill="url(#colorActual)"
                                    dot={{ fill: '#1a1d24', strokeWidth: 2, r: 4, stroke: '#ef4444' }}
                                    activeDot={{ r: 6, strokeWidth: 0, fill: '#ef4444' }}
                                />
                                <Line
                                    type="monotone"
                                    name="Target Trajectory"
                                    dataKey="target"
                                    stroke="#8b5cf6"
                                    strokeWidth={2}
                                    strokeDasharray="5 5"
                                    dot={false}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Carbon Credits Saved */}
                <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-6 col-span-1 flex flex-col min-h-[360px]">
                    <div className="flex flex-col mb-4">
                        <h3 className="font-bold text-sm">Carbon Credits Saved</h3>
                        <p className="text-[10px] text-zinc-500 mt-1">Month-over-month savings at $40/tCO2</p>
                    </div>

                    <div className="flex-1 w-full relative -ml-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={carbonCreditsData} margin={{ top: 20, right: 0, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#2e323a" vertical={false} />
                                <XAxis dataKey="month" stroke="#71717a" fontSize={10} tickLine={false} axisLine={false} />
                                <YAxis stroke="#71717a" fontSize={10} tickLine={false} axisLine={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: '10px' }} verticalAlign="top" iconType="circle" />
                                <Bar name="Credits Saved (Tons)" dataKey="credits" fill="#10b981" radius={[2, 2, 0, 0]} barSize={32} />
                                <Line type="monotone" name="Monetary Value ($)" dataKey="trend" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4, fill: '#3b82f6', strokeWidth: 0 }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Row 3: Emissions by Source and Performance Status */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Emissions by Source */}
                <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-6 flex min-h-[300px]">
                    <div className="flex-1 pr-4">
                        <h3 className="font-bold text-sm mb-6">Emissions by Source</h3>
                        <div className="space-y-4">
                            {[
                                { name: "Electricity (Scope 2)", vals: "57t - 73%", color: "bg-blue-500" },
                                { name: "Refrigerants (Scope 1)", vals: "11t - 14%", color: "bg-yellow-500" },
                                { name: "Backup Gen Diesel (Scope 1)", vals: "5t - 7%", color: "bg-red-500" },
                                { name: "Business Travel (Scope 3)", vals: "5t - 6%", color: "bg-slate-500" },
                            ].map((item, idx) => (
                                <div key={idx} className="flex justify-between items-center text-xs py-2 border-b border-[#2e323a] last:border-0">
                                    <div className="flex items-center gap-2">
                                        <span className={`w-2 h-2 rounded-full ${item.color}`} />
                                        <span className="text-zinc-300">{item.name}</span>
                                    </div>
                                    <span className="font-bold text-white">{item.vals}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="w-1/3 min-h-[220px] flex items-center justify-center">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={sourceData}
                                    innerRadius={30}
                                    outerRadius={50}
                                    paddingAngle={2}
                                    dataKey="value"
                                    stroke="none"
                                >
                                    {sourceData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Performance Status */}
                <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-6 flex flex-col min-h-[300px]">
                    <h3 className="font-bold text-sm mb-4">Performance Status</h3>
                    <div className="flex gap-4 mb-6">
                        <div className="bg-[#f87171] border border-[#ef4444] flex-1 rounded p-3 text-center flex flex-col justify-center text-white">
                            <div className="text-xl font-bold">+12.4%</div>
                            <div className="text-[9px] uppercase tracking-wider text-red-100 mt-1">Over Target</div>
                        </div>
                        <div className="bg-[#f87171] border border-[#ef4444] flex-1 rounded p-3 text-center flex flex-col justify-center text-white">
                            <div className="text-xl font-bold">9.6t</div>
                            <div className="text-[9px] uppercase tracking-wider text-red-100 mt-1">Excess CO2E</div>
                        </div>
                    </div>

                    <div className="text-[10px] text-zinc-500 uppercase flex justify-between px-2 mb-2">
                        <span>Category</span>
                        <div className="flex w-1/2 justify-between">
                            <span>Status</span>
                            <span>Variance</span>
                        </div>
                    </div>

                    <div className="space-y-1">
                        {[
                            { cat: "Server Operations", stat: "Critical", var: "+12.3%", color: "bg-red-500 text-white" },
                            { cat: "Cooling Systems", stat: "Warning", var: "+4.2%", color: "bg-orange-500 text-white" },
                            { cat: "Power Distribution", stat: "Good", var: "-1.5%", color: "bg-green-500 text-white" },
                            { cat: "Network Equipment", stat: "Good", var: "+0.5%", color: "bg-green-500 text-white" },
                        ].map((row, i) => (
                            <div key={i} className="flex justify-between items-center text-xs py-2 px-2 bg-[#14161b] rounded border border-[#2e323a]">
                                <span className="text-zinc-300 font-medium">{row.cat}</span>
                                <div className="flex w-1/2 justify-between items-center">
                                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${row.color}`}>{row.stat}</span>
                                    <span className="font-bold">{row.var}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Row 4: Timeline and Scores */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Carbon Reduction Timeline */}
                <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-6 flex flex-col min-h-[300px]">
                    <h3 className="font-bold text-sm mb-4">carbon reduction timeline</h3>

                    <div className="flex-1 w-full relative -ml-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={timelineData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorTime" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4} />
                                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="year" stroke="#71717a" fontSize={10} tickLine={false} axisLine={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Area type="monotone" name="CO2 Emission (t)" dataKey="value" stroke="#8b5cf6" strokeWidth={2} fillOpacity={1} fill="url(#colorTime)" dot={{ fill: '#1a1d24', strokeWidth: 2, r: 4, stroke: '#8b5cf6' }} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Sustainability Efficiency Scores */}
                <div className="bg-[#1a1d24] border border-[#2e323a] rounded-lg p-6 flex flex-col justify-center gap-6 min-h-[300px]">
                    <h3 className="font-bold text-sm mb-2">sustainability efficiency scores</h3>
                    <div className="grid grid-cols-2 gap-x-8 gap-y-6 px-4">
                        {[
                            { label: "ENERGY EFFICIENCY", val: 92, color: "bg-teal-400" },
                            { label: "COOLING EFFICIENCY", val: 87, color: "bg-blue-400" },
                            { label: "RESOURCE UTILIZATION", val: 94, color: "bg-teal-400" },
                            { label: "SERVER UTILIZATION", val: 89, color: "bg-teal-400" }
                        ].map((item, i) => (
                            <div key={i} className="flex flex-col gap-2">
                                <div className="flex flex-col items-center justify-center text-center">
                                    <span className="text-xl font-bold text-white transition-all">{item.val}%</span>
                                    <span className="text-[8px] uppercase tracking-widest text-zinc-400">{item.label}</span>
                                </div>
                                <div className="w-full h-1 bg-[#2e323a] rounded-full overflow-hidden mt-1">
                                    <div className={`h-full rounded-full ${item.color} shadow-[0_0_8px_rgba(45,212,191,0.5)]`} style={{ width: `${item.val}%` }}></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CarbonEmissionOverview;
